import { type FormEvent, useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  Alert,
  Box,
  Button,
  Form,
  FormField,
  FormHelp,
  Heading,
  Input,
  Label,
  Modal,
  Paragraph,
  Select,
  Textarea,
} from '@zillow/constellation-10';

import { isValidEmail } from '@/src/lib/auth/email';
import { focusFirstInvalidField } from '@/src/lib/forms/focusFirstInvalidField';
import { GatedButton } from '@/src/components/ui/GatedButton';
import { useCurrentUser, useSendFeedback } from '@/src/lib/hooks/query/useUserQuery';
import { css } from '@/styled-system/css';
import { useModalDismissProps } from '@/src/lib/hooks/ui/useModalDismissProps';

const FEEDBACK_FORM_ID = 'header-feedback-form';
const FEEDBACK_EMAIL_ID = 'feedback-email';
const FEEDBACK_CATEGORY_ID = 'feedback-category';
const FEEDBACK_COMMENT_ID = 'feedback-comment';
const COMMENT_MAX_LENGTH = 5000;

const FEEDBACK_CATEGORIES = [
  { value: 'search', labelKey: 'categorySearch' },
  { value: 'zrm', labelKey: 'categoryZrm' },
  { value: 'applications', labelKey: 'categoryApplications' },
  { value: 'payments', labelKey: 'categoryPayments' },
  { value: 'account', labelKey: 'categoryAccount' },
  { value: 'mobile', labelKey: 'categoryMobile' },
  { value: 'other', labelKey: 'categoryOther' },
] as const;

type FeedbackCategory = (typeof FEEDBACK_CATEGORIES)[number]['value'];

type FeedbackErrors = {
  userEmail?: string;
  category?: string;
  comment?: string;
};

type FeedbackModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

const formStack = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'default',
});

function getCategoryValue(value: string): FeedbackCategory | '' {
  return FEEDBACK_CATEGORIES.some((category) => category.value === value) ? (value as FeedbackCategory) : '';
}

export function FeedbackModal({ isOpen, onClose }: FeedbackModalProps) {
  const modalDismissProps = useModalDismissProps();
  const t = useTranslations('feedback');
  const { data: currentUser } = useCurrentUser();
  const sendFeedback = useSendFeedback();
  const { reset } = sendFeedback;

  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [category, setCategory] = useState<FeedbackCategory | ''>('');
  const [comment, setComment] = useState('');
  const [errors, setErrors] = useState<FeedbackErrors>({});
  const [isComplete, setIsComplete] = useState(false);
  const wasOpenRef = useRef(false);
  const userNameTouchedRef = useRef(false);
  const userEmailTouchedRef = useRef(false);

  useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current;
    wasOpenRef.current = isOpen;
    if (!isOpen) return;

    if (justOpened) {
      userNameTouchedRef.current = false;
      userEmailTouchedRef.current = false;
      setUserName(currentUser?.loggedIn ? currentUser.data.firstName : '');
      setUserEmail(currentUser?.loggedIn ? currentUser.data.email : '');
      setCategory('');
      setComment('');
      setErrors({});
      setIsComplete(false);
      reset();
      return;
    }

    if (!currentUser?.loggedIn) return;
    if (!userNameTouchedRef.current) setUserName(currentUser.data.firstName);
    if (!userEmailTouchedRef.current) setUserEmail(currentUser.data.email);
  }, [currentUser?.data.email, currentUser?.data.firstName, currentUser?.loggedIn, isOpen, reset]);

  // Name is `optional`; the other three are `required` in the markup below.
  const hasRequiredValues = !!userEmail.trim() && !!category && !!comment.trim();

  const validate = (): FeedbackErrors => {
    const nextErrors: FeedbackErrors = {};
    const trimmedEmail = userEmail.trim();

    if (!trimmedEmail) {
      nextErrors.userEmail = t('errorEmailRequired');
    } else if (!isValidEmail(trimmedEmail)) {
      nextErrors.userEmail = t('errorEmailInvalid');
    }

    if (!category) {
      nextErrors.category = t('errorCategoryRequired');
    }

    if (!comment.trim()) {
      nextErrors.comment = t('errorCommentRequired');
    }

    return nextErrors;
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextErrors = validate();
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0 || !category) {
      focusFirstInvalidField([
        nextErrors.userEmail && FEEDBACK_EMAIL_ID,
        nextErrors.category && FEEDBACK_CATEGORY_ID,
        nextErrors.comment && FEEDBACK_COMMENT_ID,
      ]);
      return;
    }

    try {
      await sendFeedback.mutateAsync({
        userName: userName.trim(),
        userEmail: userEmail.trim(),
        comment: `${category.toUpperCase()}: ${comment.trim()}`,
      });
      setIsComplete(true);
    } catch {
      // The mutation state drives the visible error message.
    }
  };

  return (
    <Modal.Root
      useDismissProps={modalDismissProps}
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
      size={{ base: 'fullScreen', lg: 'sm' }}
    >
      <Modal.Portal>
        <Modal.Backdrop>
          <Modal.Content>
            <Modal.Header>
              <Heading level={2}>{isComplete ? t('successTitle') : t('title')}</Heading>
            </Modal.Header>
            {isComplete ? (
              <>
                <Modal.Body>
                  <Paragraph>{t('successMessage')}</Paragraph>
                </Modal.Body>
                <Modal.Footer>
                  <Button tone="brand" emphasis="filled" fluid onClick={onClose}>
                    {t('done')}
                  </Button>
                </Modal.Footer>
              </>
            ) : (
              <>
                <Modal.Body>
                  <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default' }}>
                    <Paragraph css={{ color: 'text.subtle' }}>{t('subtitle')}</Paragraph>
                    <Form
                      id={FEEDBACK_FORM_ID}
                      aria-label={t('formLabel')}
                      className={formStack}
                      noValidate
                      onSubmit={handleSubmit}
                    >
                      <FormField
                        label={<Label>{t('nameLabel')}</Label>}
                        optional
                        disabled={sendFeedback.isPending}
                        control={
                          <Input
                            value={userName}
                            onChange={(event) => {
                              userNameTouchedRef.current = true;
                              setUserName(event.target.value);
                            }}
                            autoComplete="name"
                          />
                        }
                      />
                      <FormField
                        controlId={FEEDBACK_EMAIL_ID}
                        label={<Label>{t('emailLabel')}</Label>}
                        required
                        disabled={sendFeedback.isPending}
                        error={Boolean(errors.userEmail)}
                        description={
                          errors.userEmail ? <FormHelp role="alert">{errors.userEmail}</FormHelp> : undefined
                        }
                        control={
                          <Input
                            type="email"
                            value={userEmail}
                            onChange={(event) => {
                              userEmailTouchedRef.current = true;
                              setUserEmail(event.target.value);
                            }}
                            autoComplete="email"
                          />
                        }
                      />
                      <FormField
                        controlId={FEEDBACK_CATEGORY_ID}
                        label={<Label>{t('categoryLabel')}</Label>}
                        required
                        disabled={sendFeedback.isPending}
                        error={Boolean(errors.category)}
                        description={errors.category ? <FormHelp role="alert">{errors.category}</FormHelp> : undefined}
                        control={
                          <Select
                            value={category}
                            onChange={(event) => setCategory(getCategoryValue(event.target.value))}
                          >
                            <option value="">{t('categoryPlaceholder')}</option>
                            {FEEDBACK_CATEGORIES.map((feedbackCategory) => (
                              <option key={feedbackCategory.value} value={feedbackCategory.value}>
                                {t(feedbackCategory.labelKey)}
                              </option>
                            ))}
                          </Select>
                        }
                      />
                      <FormField
                        controlId={FEEDBACK_COMMENT_ID}
                        label={<Label>{t('commentLabel')}</Label>}
                        required
                        disabled={sendFeedback.isPending}
                        error={Boolean(errors.comment)}
                        description={errors.comment ? <FormHelp role="alert">{errors.comment}</FormHelp> : undefined}
                        control={
                          <Textarea
                            value={comment}
                            onChange={(event) => setComment(event.target.value)}
                            maxLength={COMMENT_MAX_LENGTH}
                            rows={5}
                          />
                        }
                      />
                    </Form>
                    {sendFeedback.isError && (
                      <Alert tone="critical" role="alert">
                        {t('errorSubmit')}
                      </Alert>
                    )}
                  </Box>
                </Modal.Body>
                <Modal.Footer>
                  <GatedButton
                    type="submit"
                    form={FEEDBACK_FORM_ID}
                    tone="brand"
                    emphasis="filled"
                    fluid
                    loading={sendFeedback.isPending}
                    disabled={!hasRequiredValues}
                    gateReason={!sendFeedback.isPending && !hasRequiredValues && t('gateHint')}
                  >
                    {t('submit')}
                  </GatedButton>
                </Modal.Footer>
              </>
            )}
            <Modal.CloseButton />
          </Modal.Content>
        </Modal.Backdrop>
      </Modal.Portal>
    </Modal.Root>
  );
}
